import React, { useState, useEffect, SyntheticEvent } from 'react';
import {
  Button,
  Typography,
  TypoWithUnderline,
  LinkHandler,
  Message3dView,
  IconHeart,
  InfoBox,
} from '@/components/atoms';
import {
  ProductExtraInfo,
  Availability,
  WholesaleMsg,
  OptionsDropdown,
  VariantDropdown,
  ConnectCustomerWithBrand,
  Price,
} from '@/components/molecules';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setItemToWishlist } from '@/store/actions/wishlist';
import { RootState } from '@/store/store';
import { useTranslation } from 'next-i18next';
import { getMultipleProductVariantQuantity } from '@/lib/api.product';
import { IProductInfoCmp } from './IProductInfoCmp';
import { ARTISANS_PATH } from '@/lib/constants';
import { transformId } from '@/utils/index';
import { IVariantDropdownOption } from '@/types/index';
import { IWishlistItem } from '@/types/wishlist';
import AddVariantsToCart from '../AddVariantsToCart';
import FavoriteIcon from '@material-ui/icons/Favorite';
import { openModal } from '@/store/actions/ui';
import { dispatchCustomEvent } from '@/utils/Impressions';
import { isOk, mapResult, ok, Result } from '@/utils/typings/result';
import { FetchError } from '@/utils/typings/errors';
import { multipleProductVariantQuantity_nodes_ProductVariant } from '@/types/shopify/multipleProductVariantQuantity';
import ProductSingleVariantCounter from '../ProductSingleVariantCounter/ProductSingleVariantCounter';
import {
  ONE_VARIANT_QTY_ADDCART,
  getVariantQuantityMinValue,
  isVariantWithCustomColor,
} from '@/utils/Product';
import ProductColorSwitcher from '../ProductColorSwitcher';
import { EXPERIMENTS } from '@/lib/experiments';
import { useAppContext } from '@/context/state';

const InfoTop = styled.div`
  text-align: left;
`;

const ConnectContainer = styled.div`
  margin-bottom: 24px;
`;

const ActionsContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-gap: 8px;
  margin-top: 16px;

  ${props => props.theme.breakpoints.up('laptop')} {
    width: 100%;
  }
  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 640px;
  }
`;

const PriceWrapper = styled.div`
  margin-bottom: 24px;
`;

const ProductExtraInfoAccordions = styled.div`
  margin-top: 16px;
  ${props => props.theme.breakpoints.up('sm')} {
    margin-top: 24px;
  }
  ${props => props.theme.breakpoints.up('laptop')} {
    width: 100%;
    max-width: 640px;
  }
`;

const ShippingInfoContainer = styled.div`
  margin: 16px 0;
`;

const ColorInfoInfoContainer = styled.div`
  margin-bottom: 8px;
`;

const UserMessage = styled.div`
  margin-bottom: 24px;
`;

const AvailabilityContainer = styled.div`
  margin-top: 32px;

  ${props => props.theme.breakpoints.up('laptop')} {
    width: 100%;
    max-width: 640px;
  }
`;

const Vendor = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  margin-bottom: 8px;
  ${props => props.theme.breakpoints.up('sm')} {
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
  }
  a {
    display: flex;
    align-items: baseline;
    span {
      margin-right: 4px;
    }
    h1 {
      border-bottom: 2px solid
        ${props => props.theme.paletteDefault.primary.main};
    }
  }
`;

const Product = styled.div`
  margin-bottom: 16px;

  ${props => props.theme.breakpoints.up('sm')} {
    margin-bottom: 24px;
  }
`;

const QuantitiesContainer = styled.div`
  & p {
    color: ${props => props.theme.paletteDefault.grey1};
  }

  display: flex;
  gap: 16px;
  margin-top: 4px;
`;

const WishListContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
  & > a {
    color: ${props => props.theme.paletteDefault.primary.main};
    text-decoration: underline;
    font-size: 14px;
  }
  ${props => props.theme.breakpoints.up('sm')} {
    justify-content: flex-start;
    gap: 8px;
  }
`;

const QuantityCounterWrapper = styled.div`
  margin-top: 16px;
  max-width: 240px;
`;

const WholesaleMsgWrapper = styled.div`
  margin-bottom: 16px;
`;

const ExtraOptionsContainer = styled.div`
  margin-top: 16px;
  max-width: 400px;
`;

const BrandNote = styled.div`
  margin: 16px 0;
  max-width: 640px;
`;

const ProductInfo: React.FC<IProductInfoCmp> = props => {
  const {
    isUsUser,
    currency,
    selectedOptions,
    onChangeHandler,
    availabilityInfos,
    show3dicon,
    openFutureFashion,
    variantAttributes,
    shippingInfo,
    brandSlug,
    product: {
      title,
      vendor,
      productMetafields: { userMessage },
      variant: { id: variantId },
      prices,
      productDescription,
      options,
      handle,
      id: productId,
      extraInfo,
      minimumOrderQuantity,
      deliveryTime,
      variants,
      shortDescription,
      artisan_registry_id,
      artisanProductDetail,
    },
  } = props;

  const { t } = useTranslation('product');
  const dispatch = useDispatch();
  const empi = productId!;
  const epi = variantId!;
  const du = handle!;

  const artisanColor = artisanProductDetail?.color || null;

  const isCustomColor = isVariantWithCustomColor(handle);

  const extraOptions =
    options?.filter(
      item =>
        item.name.toLowerCase() !== 'color' &&
        item.name.toLowerCase() !== 'size'
    ) ?? [];

  const extraOptionName = extraOptions[0]?.name.toLowerCase(); // ! here strong assumption that there is only one "extra" option. in addition to "color" and "size"

  const extraOptionNames = extraOptions.map(eo => eo.name.toLowerCase());

  const selectedOptionsKeys = Object.keys(selectedOptions || []);

  const hasAllOptionsSelected = extraOptionNames.every(elem =>
    selectedOptionsKeys.includes(elem)
  );

  const isExtraOptionIncluded =
    selectedOptions && extraOptionName && hasAllOptionsSelected;

  const [payload, setPayload] = useState<IWishlistItem>({ empi, epi, du });
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [variantDropdownOptions, setVariantDropdownOptions] = useState<
    IVariantDropdownOption[]
  >([]);
  const [chosenVariants, setChosenVariants] = useState<{
    [key: string]: number;
  }>({});
  const [showTotal, setShowTotal] = useState(false);

  const wishlist = useSelector(
    (state: RootState) => state.wishlistReducer.wishlist
  );
  const isLogged = useSelector(
    (state: RootState) => state.userReducer.isLogged
  );

  const customer = useSelector(
    (state: RootState) => state.userReducer.customer
  );
  const context = useAppContext();
  const enableWishlist = context?.settings
    ? context.settings['enableWishlist']
    : true;

  useEffect(() => {
    setPayload({ empi, epi, du });
  }, [empi, epi, du]);

  useEffect(() => {
    if (wishlist) {
      if (wishlist.length > 0) {
        const result = wishlist.filter(
          (w: IWishlistItem) => w.empi === payload.empi
        );
        if (result.length > 0) {
          setIsInWishlist(true);
        } else {
          setIsInWishlist(false);
        }
      }
    }
  }, [wishlist, payload]);

  useEffect(() => {
    getVariantDropdownList()
      .then(result => {
        if (isOk(result)) {
          setChosenVariants({}); // reset chosen variants when colors or extra options change

          setVariantDropdownOptions(result.result);
        } else {
          console.error(result.error);
        }
      })
      .catch(err => console.error(err));
  }, [selectedOptions]);

  const getVariantDropdownList = async (): Promise<
    Result<IVariantDropdownOption[], FetchError>
  > => {
    if ((selectedOptions && !extraOptionName) || isExtraOptionIncluded) {
      const variantDropdownOptions = variants
        .map(variant => {
          return {
            variant_id: variant.variantId
              ? transformId(variant.variantId, 'ProductVariant')
              : '',
            variant_options: variant.options?.reduce(
              (acc, curr) => ({
                ...acc,
                [curr.name.toLowerCase()]: curr.value,
              }),
              {} as { [key: string]: string }
            ),
          };
        })
        .filter(item => item.variant_id)
        .filter(item =>
          Object.keys(selectedOptions).every(key => {
            return selectedOptions[key] === item.variant_options![key];
          })
        );

      const quantities = await getMultipleProductVariantQuantity(
        variantDropdownOptions.map(item => item.variant_id)
      );

      return mapResult(quantities, quantities => {
        const orderedSizes = options?.find(
          item => item.name.toLowerCase() === 'size'
        )?.values;

        return variantDropdownOptions
          .map(item => ({
            ...item,
            ...(quantities.nodes.find(
              obj =>
                obj?.__typename === 'ProductVariant' &&
                obj?.id === item.variant_id
            ) as multipleProductVariantQuantity_nodes_ProductVariant),
          }))
          .map(({ id, ...item }) => item)
          .sort(
            (a, b) =>
              (orderedSizes?.indexOf(a.variant_options?.size ?? '') ?? 0) -
              (orderedSizes?.indexOf(b.variant_options?.size ?? '') ?? 0)
          );
      });
    }
    return ok([]);
  };

  const onChangeQuantity = (quantity: number, variantId: string) => {
    if (quantity === 0) {
      const newSizes = { ...chosenVariants };
      delete newSizes[variantId];
      setChosenVariants({ ...newSizes });
    } else {
      setChosenVariants({ ...chosenVariants, [variantId]: quantity });
    }
  };

  const onChangeQuantityNoSize = (quantity: number, variantId: string) => {
    setChosenVariants({ [variantId]: quantity });
  };

  const addToWishlistHandler = (e: SyntheticEvent) => {
    e.preventDefault();
    if (isLogged) {
      dispatch(setItemToWishlist(payload, title));
      setIsInWishlist(true);
    } else {
      dispatch(openModal('logReg', '', 'register'));
      dispatchCustomEvent('addToWishlistNotLogged', {
        productTitle: title,
      });
    }
  };

  const onVariantDropdownChange = (expanded: boolean) => {
    setShowTotal(expanded);
  };
  let defaultQuantity: number = ONE_VARIANT_QTY_ADDCART;
  const qtyExperiment = EXPERIMENTS['product_default_quantity'];
  const experimentID = qtyExperiment.id;
  const experiments = window.Mirta.experiments;
  if (experiments && experimentID in window.Mirta.experiments) {
    const variationID: string = experiments[experimentID];
    if (variationID in qtyExperiment['variations']) {
      defaultQuantity = qtyExperiment['variations'][
        variationID as keyof typeof qtyExperiment['variations']
      ] as number;
    }
  }
  const initialQuantity = getVariantQuantityMinValue(
    minimumOrderQuantity || null,
    variantDropdownOptions?.[0]?.currentlyNotInStock || false,
    variantDropdownOptions?.[0]?.quantityAvailable || 0,
    defaultQuantity
  );

  const productInfo = `${title} - ${shortDescription}`;

  const totalChosenPieces = Object.values(chosenVariants).reduce(
    (acc, curr) => acc + curr,
    0
  );

  return (
    <>
      <InfoTop>
        {isLogged && customer && (
          <ConnectContainer>
            <ConnectCustomerWithBrand
              customerEmail={customer.email}
              brandId={artisan_registry_id}
              company={customer.defaultAddress.company}
            />
          </ConnectContainer>
        )}
        {isInWishlist && isLogged && (
          <WishListContainer>
            <IconHeart width={22} height={22} isFilled={true} />
            <LinkHandler href="/wishlist">
              {t('cta.added-wishlist')}
            </LinkHandler>
          </WishListContainer>
        )}
        <Vendor>
          <LinkHandler href={`/${ARTISANS_PATH}/${brandSlug}`}>
            <a>
              <Typography component="h1" datatype="subtitle_2">
                {vendor}
              </Typography>
            </a>
          </LinkHandler>
        </Vendor>
        <Product>
          <Typography datatype="subtitle_2">{productInfo}</Typography>
        </Product>
        {prices && (
          <PriceWrapper>
            {currency && (
              <Price prices={prices} currency={currency} template="medium" />
            )}
            {isUsUser && <div>{t('product:messages.us-price')}</div>}
          </PriceWrapper>
        )}
        <WholesaleMsgWrapper>
          <WholesaleMsg />
        </WholesaleMsgWrapper>

        {show3dicon && (
          <div onClick={openFutureFashion}>
            <Message3dView />
          </div>
        )}

        {userMessage && (
          <UserMessage>
            <TypoWithUnderline datatype="body_1" size={'big'}>
              {userMessage.toUpperCase()}
            </TypoWithUnderline>
          </UserMessage>
        )}
        {shippingInfo && (
          <ShippingInfoContainer>
            <Typography datatype="caption_1">{shippingInfo}</Typography>
          </ShippingInfoContainer>
        )}
        {artisanColor && !isCustomColor && (
          <ColorInfoInfoContainer>
            <Typography datatype="body_1">
              {t('colors.color')}: {artisanColor}
            </Typography>
          </ColorInfoInfoContainer>
        )}

        {variantAttributes && variantAttributes.length > 1 && (
          <ProductColorSwitcher
            variantAttributes={variantAttributes}
            selectedHandle={handle}></ProductColorSwitcher>
        )}
      </InfoTop>

      {extraOptions?.length > 0 && (
        <ExtraOptionsContainer>
          <OptionsDropdown
            options={extraOptions}
            selectedColor={selectedOptions?.color}
            onChangeHandler={onChangeHandler}
          />
        </ExtraOptionsContainer>
      )}
      {variantDropdownOptions?.length > 0 ? (
        variantDropdownOptions?.length === 1 &&
        !options.find(o => o.name.toLowerCase() === 'size') ? (
          <QuantityCounterWrapper>
            <ProductSingleVariantCounter
              onChangeQuantityNoSize={onChangeQuantityNoSize}
              variantInfo={variantDropdownOptions[0]}
              variantChosenQuantity={
                chosenVariants[variantDropdownOptions[0]?.variant_id]
              }
              initialQuantity={initialQuantity}
              extraOptionIncluded={!!isExtraOptionIncluded ? true : false}
              {...(!!isExtraOptionIncluded ? { extraOptionName } : {})}
            />
          </QuantityCounterWrapper>
        ) : (
          <VariantDropdown
            options={variantDropdownOptions}
            chosenVariants={chosenVariants}
            onChangeQuantity={onChangeQuantity}
            onDropdownChange={onVariantDropdownChange}
          />
        )
      ) : null}

      <QuantitiesContainer>
        {minimumOrderQuantity > 1 ? (
          <Typography datatype="body_1">
            {t('minimum_order_quantity')} {minimumOrderQuantity}
          </Typography>
        ) : null}
        {variantDropdownOptions?.length >= 2 && showTotal && (
          <Typography datatype="body_1">
            {`${t('total')}: ${totalChosenPieces}`}
          </Typography>
        )}
      </QuantitiesContainer>

      <ActionsContainer>
        <AddVariantsToCart chosenVariants={chosenVariants} />

        {enableWishlist && (
          <Button
            onClick={addToWishlistHandler}
            disabled={isInWishlist && isLogged}
            variant="outlined">
            <FavoriteIcon />
          </Button>
        )}
      </ActionsContainer>

      {deliveryTime && (
        <AvailabilityContainer>
          <Availability
            delivery_time={deliveryTime}
            availabilityInfos={availabilityInfos}
          />
        </AvailabilityContainer>
      )}

      {extraInfo && (
        <BrandNote>
          <InfoBox title={t('product:brand-note')} icon={false}>
            {extraInfo}
          </InfoBox>
        </BrandNote>
      )}

      <ProductExtraInfoAccordions>
        <ProductExtraInfo extraInfo={productDescription} />
      </ProductExtraInfoAccordions>
    </>
  );
};

export default ProductInfo;
